import type {
  ChapterEndData,
  ChapterEndPerfRow,
  ChapterEndScene,
  CommandItem,
  Scenario,
  Scene,
  TextOverlayOption,
  TextOverlayScene,
} from '../types';
import { getSceneById, resolveNextSceneId } from './scene-flow';

export const DEFAULT_CHAPTER_END_CONTINUE_TEXT = 'Continuer';
export const DEFAULT_CHAPTER_END_PERF_NOTE = 'TON PARCOURS';
export const CHAPTER_END_PRIMARY_ACTION_LABEL = 'Chapitre suivant →';

function isPerfRowVisible(
  row: ChapterEndPerfRow,
  evaluate: (condition: string) => boolean,
): boolean {
  if (!row.condition) return true;
  return evaluate(row.condition);
}

/**
 * Keep the first true row per label (if/else-if/else chain).
 * Display order follows first occurrence of each label in the source array.
 */
export function selectVisiblePerfRows(
  rows: ChapterEndPerfRow[],
  evaluate: (condition: string) => boolean,
): ChapterEndPerfRow[] {
  const seenLabels = new Set<string>();
  const visible: ChapterEndPerfRow[] = [];

  for (const row of rows) {
    if (seenLabels.has(row.label)) continue;
    if (!isPerfRowVisible(row, evaluate)) continue;
    seenLabels.add(row.label);
    visible.push(row);
  }

  return visible;
}

export function resolveChapterEndPerfNote(perfNote?: string): string {
  const trimmed = perfNote?.trim();
  return trimmed || DEFAULT_CHAPTER_END_PERF_NOTE;
}

/** Libellé du bouton principal : « Mission … » devient « Chapitre suivant ». */
export function resolveChapterEndPrimaryActionLabel(continueText: string): string {
  const trimmed = continueText.trim();
  if (!trimmed || /^continuer$/i.test(trimmed) || /mission/i.test(trimmed)) {
    return CHAPTER_END_PRIMARY_ACTION_LABEL;
  }
  return trimmed;
}

export type ChapterEndPresentation = {
  data: ChapterEndData;
  continueText: string;
  /** Actions héritées d'une ancienne option « continuer » (sans goto). */
  continueActions?: CommandItem[];
};

function isMenuOption(opt: TextOverlayOption): boolean {
  return opt.goto === '__MAIN_MENU__' || opt.goto === '__MENU__';
}

function legacyContinueOption(options?: TextOverlayOption[]): TextOverlayOption | null {
  if (!options?.length) return null;
  return options.find((opt) => !isMenuOption(opt)) ?? options[0] ?? null;
}

export function resolveChapterEndContinueText(
  scene: Pick<ChapterEndData, 'continueText'> & { options?: TextOverlayOption[] } | null
): string {
  if (!scene) return DEFAULT_CHAPTER_END_CONTINUE_TEXT;
  if (scene.continueText?.trim()) return scene.continueText.trim();
  const legacy = legacyContinueOption(scene.options);
  if (legacy?.text?.trim()) return legacy.text.trim();
  return DEFAULT_CHAPTER_END_CONTINUE_TEXT;
}

export function defaultChapterEndData(): ChapterEndData {
  return {
    eyebrow: '',
    heading: '',
    missionIndex: 1,
    missionTotal: 1,
    keyIdea: '',
    reflexes: [],
    perfRows: [],
    continueText: DEFAULT_CHAPTER_END_CONTINUE_TEXT,
  };
}

/** Bloc chapterEnd par défaut (sans id — l'appelant le fournit). */
export function defaultChapterEndItem(): Omit<ChapterEndScene, 'id'> {
  return {
    type: 'chapterEnd',
    ...defaultChapterEndData(),
  };
}

export function defaultChapterEndScene(): ChapterEndScene {
  return {
    id: 'chapter_end',
    type: 'chapterEnd',
    ...defaultChapterEndData(),
  };
}

export function resolveChapterEnd(scene: Scene | null): ChapterEndPresentation | null {
  if (!scene) return null;
  if (scene.type === 'chapterEnd') {
    const { type: _type, id: _id, options, ...data } = scene;
    const legacy = legacyContinueOption(options);
    return {
      data,
      continueText: resolveChapterEndContinueText(scene),
      continueActions: legacy?.actions,
    };
  }
  if (scene.type === 'textOverlay' && scene.overlayType === 'chapterEnd' && scene.chapterEnd) {
    const legacy = legacyContinueOption(scene.options);
    return {
      data: scene.chapterEnd,
      continueText: resolveChapterEndContinueText({ ...scene.chapterEnd, options: scene.options }),
      continueActions: legacy?.actions,
    };
  }
  return null;
}

export function isChapterEndScene(scene: Scene | null): scene is ChapterEndScene {
  return scene?.type === 'chapterEnd';
}

export function isLegacyChapterEndOverlay(scene: Scene | null): scene is TextOverlayScene {
  return (
    scene?.type === 'textOverlay'
    && scene.overlayType === 'chapterEnd'
    && Boolean(scene.chapterEnd)
  );
}

export function sceneIsChapterEnd(scene: Scene): boolean {
  return resolveChapterEnd(scene) !== null;
}

/** Next scene id when a dialogue should open the chapter-end bridge instead of advancing. */
export function findChapterEndNavigationTarget(scenario: Scenario, scene: Scene | null): string | null {
  if (!scene || scene.type !== 'dialogue') return null;
  const targetId = scene.goto || resolveNextSceneId(scenario, scene.id);
  if (!targetId) return null;
  const target = getSceneById(scenario, targetId);
  return target && sceneIsChapterEnd(target) ? targetId : null;
}
